import type { Step, Tour } from "nextstepjs";

import { AppConfig } from "@/config/app.config";
import { RouteConfig } from "@/config/route.config";

/**
 * Tour Configuration for Demo Mode
 *
 * This defines a single comprehensive tour that guides users through
 * all demo pages. NextStepJS handles navigation between pages automatically
 * using the nextRoute and prevRoute properties.
 */

export enum TourSelectors {
  UserAvatarNavSection = "tour-user-avatar-nav-section",
  // Desktop selectors
  WatchlistSection = "tour-watchlist-nav-section",
  WatchlistGroupMenu = "tour-watchlist-group-menu",
  WatchlistTableRow = "tour-watchlist-table-row",
  WatchlistTableLastColumn = "tour-watchlist-table-last-column",
  SearchJobPositionInput = "tour-search-job-position-input",
  JobFitSection = "tour-job-fit-section",
  IntroductionSection = "tour-introduction-section",
  ProjectsSection = "tour-projects-section",
  SkillsDiagramSection = "tour-skills-diagram-section",
  YourNumbersSection = "tour-your-numbers-section",
  AnalyticsSection = "tour-analytics-section",
  // Mobile-specific selectors
  HamburgerMenu = "tour-hamburger-menu",
  LossdogGrade = "tour-lossdog-grade",
  RewardsSection = "tour-rewards-section",
  CeoVsEmployeeSection = "tour-ceo-vs-employee-section",
  PortfolioSection = "tour-portfolio-section",
  LiveshowSection = "tour-liveshow-section",
}

const defaultOptions: Partial<Step> = {
  icon: null,
  title: "",
  showControls: true,
  showSkip: false, // Skip functionality removed from UI
  pointerPadding: 0,
  pointerRadius: 0,
  // Allow keyboard control (including Escape) in development only
  blockKeyboardControl: AppConfig.isProduction,
};

/**
 * Custom highlight styles to apply to tour elements
 * Values can be numbers (converted to px) or CSS strings
 */
export type HighlightStyles = {
  /** Padding to apply (useful for title-only highlights to provide breathing room) */
  padding?: string | number;
  /** Margin to apply (positive expands, negative shrinks the highlight box) */
  margin?: string | number;
  borderRadius?: string | number;
};

export type TourStepConfig = Pick<Step, "selector" | "content" | "side" | "nextRoute" | "prevRoute"> & {
  /**
   * Custom highlight styles to apply to the tour element
   * Use this to fine-tune how the highlight box appears around specific elements
   */
  highlight?: HighlightStyles;
  preventHighlight?: boolean;
  scrollToTop?: boolean;
};

export const demoTourSteps: TourStepConfig[] = [
  // Welcome step - starts on Profile page
  {
    selector: `#${TourSelectors.UserAvatarNavSection}`,
    content:
      "Welcome to Lossdog! Let's Take A tour of the app. First, Let's Start with the profile section, Where you will be able to review your information, Upload relevant projects and Share and download your collected content!",
    side: "top-left",
    preventHighlight: true,
  },
  // Profile page steps
  {
    selector: `#${TourSelectors.IntroductionSection}`,
    content:
      "Here, We generate your personal bio pulled from your resume, don't hesitate to edit this with your own personal flavor.",
    side: "right",
    highlight: { padding: 3, margin: "0 0 7px 0" },
  },
  {
    selector: `#${TourSelectors.ProjectsSection}`,
    content:
      "Here you can Add any project You've worked on... a coding Project, a cooking presentation, a video of you playing banjo, anything you like!",
    side: "right",
    highlight: { padding: 3 },
  },
  {
    selector: `#${TourSelectors.SkillsDiagramSection}`,
    content: "Of course, you have the freedom to edit any other section of your profile as well.",
    side: "right",
    highlight: { padding: 3 },
  },
  {
    selector: `#${TourSelectors.YourNumbersSection}`,
    content:
      "Here It Gets Exciting. This Section Lets you compare your Value to your industry's average, For example, here Tavi is worth $138,974 as a Financial Analyst in Chicago, which is above average in her field, but following our guidance, she has the potential to reach $149,764.",
    side: "bottom-right",
    highlight: { padding: 10 },
    scrollToTop: true,
    nextRoute: RouteConfig.Demo.Watchlist,
  },
  // Watchlist page steps
  {
    selector: `#${TourSelectors.WatchlistSection}`,
    content:
      "Next, it's our watchlist where you are able to take a look at current average wages for most jobs in the United States.",
    side: "top-left",
    prevRoute: RouteConfig.Demo.Profile,
  },
  {
    selector: `#${TourSelectors.WatchlistGroupMenu}`,
    content:
      "We've put job titles into groups, here you can select which watchlist you want to look at by industry. You can also tailor the numbers based on your location and experience.",
    side: "bottom-left",
    highlight: { borderRadius: "9999px" },
    scrollToTop: true,
  },
  {
    selector: `#${TourSelectors.WatchlistTableRow}`,
    content: "For example, an Auditor Appraiser with 11 years of experience should make around $99,000 in Chicago.",
    side: "bottom",
    highlight: { margin: 3 },
  },
  {
    selector: `#${TourSelectors.WatchlistTableLastColumn}`,
    content:
      "Our numbers update in real time. this column represents the daily change in dollar amounts for each position.",
    side: "bottom",
    highlight: { margin: "3px 0 0 -3px" },
  },
  {
    selector: `#${TourSelectors.SearchJobPositionInput}`,
    content:
      'If you\'re curious about a position in particular, you can always look for it here. For example a Search for "Financial Analyst".',
    side: "bottom",
  },
  {
    selector: `#${TourSelectors.JobFitSection}`,
    content:
      "Once you've selected a position in particular, you can see the evolution of its value as well as the value of a similar position, you can also check how well your profile fits to this position.",
    side: "left",
    scrollToTop: true,
    nextRoute: RouteConfig.Demo.Analytics,
    prevRoute: RouteConfig.Demo.Watchlist,
  },
  // Analytics navigation step
  {
    selector: `#${TourSelectors.AnalyticsSection}`,
    content: "In the analytics section, you get personalized analytics and a game plan to improve your Fair Value.",
    side: "top-left",
    nextRoute: RouteConfig.Demo.MyRewards,
    prevRoute: RouteConfig.Demo.Watchlist,
  },
  // My Rewards page steps
  {
    selector: `#${TourSelectors.RewardsSection}`,
    content:
      "IN the reward section, you'll be able to find and claim the treats you've unlocked thanks to your fidelity.",
    side: "bottom-left",
    nextRoute: RouteConfig.Demo.CeoVsEmployee,
    prevRoute: RouteConfig.Demo.Analytics,
  },
  // Ceo Vs Employee page steps
  {
    selector: `#${TourSelectors.CeoVsEmployeeSection}`,
    content:
      "Here is our CEO vs employee section, this is where we list all S&P 500 ceo's salaries and compare it to average employee salaries.",
    side: "bottom-left",
    nextRoute: RouteConfig.Demo.Portfolio,
    prevRoute: RouteConfig.Demo.MyRewards,
  },
  // Portfolio page steps
  {
    selector: `#${TourSelectors.PortfolioSection}`,
    content:
      "HEre is where tom sosnoff lets you know what he has in his portfolio so you get pointers about how to diversify your own and how the market works.",
    side: "top-left",
    nextRoute: RouteConfig.Demo.Liveshow,
    prevRoute: RouteConfig.Demo.CeoVsEmployee,
  },
  // Liveshow page steps
  {
    selector: `#${TourSelectors.LiveshowSection}`,
    content:
      "Finally we also run our daily live show directly on our platform, so watch, call, annoy us monday - thursday at 9am CDT.",
    side: "top-left",
    prevRoute: RouteConfig.Demo.Portfolio,
  },
];

/**
 * Tour identifier for use with useNextStep hook
 */
export const DEMO_TOUR_ID = "demo-tour";

/**
 * Tour configuration object for NextStep (Desktop)
 */
export const demoTourConfig: Tour = {
  tour: DEMO_TOUR_ID,
  steps: demoTourSteps.map((step) => ({
    ...step,
    ...defaultOptions,
  })) as Step[],
};

/**
 * Mobile Tour Configuration
 *
 * A separate tour configuration for mobile devices with:
 * - Hamburger menu for navigation (instead of sidebar items)
 * - Skipped watchlist-specific steps (mobile watchlist in progress)
 * - Mobile-optimized highlight styles
 */
export const demoTourMobileSteps: TourStepConfig[] = [
  // Welcome step - starts on Profile page
  {
    content:
      "Welcome to Lossdog! Let's take a tour of the app. First, let's start with the profile section, where you will be able to review your information, upload relevant projects and share and download your collected content!",
    side: "top",
    preventHighlight: true,
  },
  // Profile page steps
  {
    selector: `#${TourSelectors.IntroductionSection}`,
    content:
      "Here, we generate your personal bio pulled from your resume, don't hesitate to edit this with your own personal flavor.",
    side: "bottom",
    highlight: { padding: 3, margin: "30px 0px 0px 0px" },
  },
  {
    selector: `#${TourSelectors.ProjectsSection}`,
    content:
      "Here you can add any project you've worked on... a coding project, a cooking presentation, a video of you playing banjo, anything you like!",
    side: "bottom",
    highlight: { padding: 3, margin: "30px 0px 20px 0px" },
  },
  {
    selector: `#${TourSelectors.SkillsDiagramSection}`,
    content: "Of course, you have the freedom to edit any other section of your profile as well.",
    side: "bottom",
    highlight: { padding: 3, margin: "30px 0px 0px 0px" },
  },
  {
    selector: `#${TourSelectors.YourNumbersSection}`,
    content:
      "Here it gets exciting. This section lets you compare your value to your industry's average. For example, Younes is worth $245,674 as a Lead Product Developer in Chicago, which is above average in their field, but following our guidance has the potential to reach $304,456.",
    side: "bottom",
    highlight: { padding: 10 },
    scrollToTop: true,
    nextRoute: RouteConfig.Demo.Watchlist,
  },
  // Watchlist steps for mobile
  {
    selector: `#${TourSelectors.HamburgerMenu}`,
    content:
      "Next, it's our watchlist where you are able to take a look at current average wages for most jobs in the United States.",
    side: "bottom",
    preventHighlight: true,
    prevRoute: RouteConfig.Demo.Profile,
  },
  {
    selector: `#${TourSelectors.WatchlistGroupMenu}`,
    content:
      "We've put job titles into groups, here you can select which watchlist you want to look at by industry. You can also tailor the numbers based on your location and experience.",
    side: "bottom",
    highlight: { borderRadius: "9999px" },
  },
  {
    selector: `#${TourSelectors.WatchlistTableRow}`,
    content: "For example a Financial Advisor with 5-10 years of experience should make around $112,000 in Chicago.",
    side: "bottom",
    highlight: { margin: "3px 3px 3px 3px" },
  },
  {
    selector: `#${TourSelectors.SearchJobPositionInput}`,
    content:
      'If you\'re curious about a position in particular, you can always look for it here. For example a search for "Financial Analyst".',
    side: "bottom",
    nextRoute: RouteConfig.Demo.Analytics,
  },
  // Note: JobFitSection is skipped on mobile (it's inside a drawer that's not open by default)

  // Hamburger menu - navigate to Analytics
  {
    selector: `#${TourSelectors.LossdogGrade}`,
    content: "In the analytics section, you get personalized analytics and a game plan to improve your fair value.",
    side: "bottom",
    nextRoute: RouteConfig.Demo.MyRewards,
    prevRoute: RouteConfig.Demo.Watchlist,
    preventHighlight: true,
  },
  {
    selector: `#${TourSelectors.RewardsSection}`,
    content:
      "In the reward section, you'll be able to find and claim the treats you've unlocked thanks to your fidelity.",
    side: "bottom-left",
    nextRoute: RouteConfig.Demo.CeoVsEmployee,
    prevRoute: RouteConfig.Demo.Analytics,
    preventHighlight: true,
  },
  // Ceo Vs Employee page steps
  {
    selector: `#${TourSelectors.CeoVsEmployeeSection}`,
    content:
      "Here is our CEO vs employee section, this is where we list all S&P 500 CEO's salaries and compare it to average employee salaries.",
    side: "bottom-left",
    nextRoute: RouteConfig.Demo.Portfolio,
    prevRoute: RouteConfig.Demo.MyRewards,
    preventHighlight: true,
  },
  // Portfolio page steps
  {
    selector: `#${TourSelectors.PortfolioSection}`,
    content:
      "Here is where Tom Sosnoff lets you know what he has in his portfolio so you get pointers about how to diversify your own and how the market works.",
    side: "bottom-left",
    nextRoute: RouteConfig.Demo.Liveshow,
    prevRoute: RouteConfig.Demo.CeoVsEmployee,
    preventHighlight: true,
  },
  // Liveshow page steps
  {
    selector: `#${TourSelectors.HamburgerMenu}`,
    content:
      "Finally we also run our daily live show directly on our platform, so watch, call, annoy us Monday - Thursday at 9am CDT.",
    side: "bottom-left",
    prevRoute: RouteConfig.Demo.Portfolio,
    preventHighlight: true,
  },
];

/**
 * Mobile tour identifier
 */
export const DEMO_TOUR_MOBILE_ID = "demo-tour-mobile";

/**
 * Tour configuration object for NextStep (Mobile)
 */
export const demoTourMobileConfig: Tour = {
  tour: DEMO_TOUR_MOBILE_ID,
  steps: demoTourMobileSteps.map((step) => ({
    ...step,
    ...defaultOptions,
  })) as Step[],
};
